/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box, Button, Text } from "@chakra-ui/react"
import { useAtom, useSetAtom } from "jotai"
import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Paths } from "../../../common/commonConstants"
import { Colors } from "../../../theme/colors"
import useFetchFilteredFMP from "../common.fmp.hooks/fetchFilteredFMP.data.hook"
import { formData } from "../common.fmp.hooks/formData.store"
import { useGetHTMLforPDFDownload } from "../common.fmp.hooks/pdfDownload.hook"
import { pdfGeneratorAtom } from "../common.fmp.hooks/pdfGenerator.store"

const LABEL_WIDTH = "150px"

const InfoField = ({ label, value }: { label: string; value: string }): JSX.Element => (
  <Box display="grid" gridTemplateColumns={`${LABEL_WIDTH} 1fr`} columnGap="8px">
    <Text variant="RCaptionBold" color={Colors.Primary.c1} fontSize="14px" whiteSpace="nowrap" textAlign="right">
      {label}
    </Text>
    <Text fontSize="14px" color={Colors.Secondary.c2} whiteSpace="pre-wrap" wordBreak="break-word" textAlign="left">
      {value || "-"}
    </Text>
  </Box>
)

interface ViewFireManagementPlanFormProps {
  fmpId?: number
  refToPass: React.RefObject<HTMLDivElement>
}
export const ViewFireManagementPlanForm: React.FC<ViewFireManagementPlanFormProps> = ({ fmpId, refToPass }): JSX.Element => {
  const navigate = useNavigate()
  const [formDataAtom, setFormDataAtom] = useAtom(formData)
  const { generate: generateHTMLForPDF } = useGetHTMLforPDFDownload(refToPass)
  const setPdfGenerator = useSetAtom(pdfGeneratorAtom)

  const navigateToEditFMP = (): void => {
    if (fmpId) {
      navigate(Paths.FMP.EDIT_FMP, { state: { fmpId: Number(fmpId) } })
    }
  }

  useEffect(() => {
    setPdfGenerator(() => generateHTMLForPDF)
    return () => {
      setPdfGenerator(null)
    }
  }, [generateHTMLForPDF, setPdfGenerator])

  const { filteredFMP } = useFetchFilteredFMP(fmpId)
  useEffect(() => {
    if (filteredFMP?.length) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { project_name, project_address, subcontractor_address, subcontractor_name } = filteredFMP?.[0] ?? {
        project_name: "",
        project_address: "",
        subcontractor_name: "",
        subcontractor_address: ""
      }
      setFormDataAtom((prevState: any) => ({
        ...prevState,
        projectName: project_name ?? "",
        projectAddress: project_address ?? "",
        companyName: subcontractor_name ?? "",
        companyAddress: subcontractor_address ?? ""
      }))
    }
  }, [filteredFMP, setFormDataAtom])
  useEffect(() => {
    const emptyObject = Object.keys(formData)?.reduce((acc: Record<string, string>, key) => {
      return { ...acc, [key]: "" }
    }, {})
    setFormDataAtom(prev => ({
      ...prev,
      ...emptyObject
    }))
  }, [formData, setFormDataAtom])

  return (
    <Box mt="24px">
      <Box display="grid" gridTemplateColumns="1fr 1fr auto 1fr" gridTemplateRows="auto auto" columnGap="24px" rowGap="8px">
        <Box gridColumn="1" gridRow="1">
          <InfoField label="Project Name:" value={formDataAtom.projectName} />
        </Box>
        <Box gridColumn="2" gridRow="1">
          <InfoField label="Contractor Name:" value={formDataAtom.companyName} />
        </Box>
        <Box gridColumn="3" gridRow="1">
          <InfoField label="Revision:" value={formDataAtom.revision} />
        </Box>
        <Box gridColumn="4" gridRow="1 / span 2" display="flex" alignItems="flex-start" justifyContent="flex-end">
          <Button variant="Button1RegularWithShadow" whiteSpace="nowrap" onClick={navigateToEditFMP}>
            Edit Plan
          </Button>
        </Box>
        <Box gridColumn="1" gridRow="2">
          <InfoField label="Project Address:" value={formDataAtom.projectAddress} />
        </Box>
        <Box gridColumn="2" gridRow="2">
          <InfoField label="Contractor Address:" value={formDataAtom.companyAddress} />
        </Box>
      </Box>
    </Box>
  )
}
